
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLanguage } from '@/contexts/LanguageContext';

const createProfileSchema = (t: (key: string) => string) => z.object({
  username: z.string().min(2, t('editProfile.validationName')),
  email: z.string().email(t('editProfile.validationEmail')).optional(),
});

const EditProfileForm: React.FC = () => {
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  const profileSchema = createProfileSchema(t);

  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: userProfile?.full_name || "",
      email: user?.email || "",
    },
  });

  useEffect(() => {
    // Update form when user profile is loaded
    if (userProfile?.full_name) {
      form.setValue("username", userProfile.full_name);
    }
    if (user?.email) {
      form.setValue("email", user.email);
    }
  }, [userProfile, user, form]);

  const onSubmit = async (values: z.infer<typeof profileSchema>) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          full_name: values.username,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success(t('editProfile.success'));
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast.error(t('editProfile.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('editProfile.title')}</CardTitle>
        <CardDescription>
          {t('editProfile.description')}
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('editProfile.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('editProfile.email')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? t('editProfile.saving') : t('editProfile.save')}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default EditProfileForm;
